import { normalizeResources } from './resources';
import { isSessionId } from './events';

// One manager per server-issued session. POST replaces the whole set, so never overlap.
export function createSubscriptionManager({ sessionId, send, onSynced = () => {}, onError = () => {},
  renewalMs = 5 * 60 * 1000, retryMs = 10000 }) {
  if (!isSessionId(sessionId)) throw new Error('INVALID_REALTIME_SESSION');
  let desired = [];
  let revision = 0;
  let acknowledged = -1;
  let running = false;
  let disposed = false;
  let timer;
  let controller;
  const schedule = delay => {
    clearTimeout(timer);
    timer = setTimeout(() => { acknowledged = -1; void pump(); }, delay);
  };
  async function pump() {
    if (disposed || running || acknowledged === revision) return;
    running = true;
    clearTimeout(timer);
    let sentRevision;
    try {
      while (!disposed && acknowledged !== revision) {
        sentRevision = revision;
        const sentResources = [...desired];
        controller = new AbortController();
        await send({ sessionId, subscriptions: sentResources.map(resource => ({ resource, id: 'LIST' })) }, controller.signal);
        if (disposed) return;
        acknowledged = sentRevision;
        if (acknowledged === revision) onSynced(sentResources);
      }
      if (!disposed) schedule(renewalMs);
    } catch (error) {
      if (!disposed) {
        onError({ status: error.status || 0, code: error.code || 'SUBSCRIPTION_FAILED' });
        if (disposed) return;
        if (revision !== sentRevision) schedule(0);
        else if (![401, 403, 404].includes(error.status)) schedule(retryMs);
      }
    } finally { running = false; }
  }
  return {
    setResources(resources) {
      if (disposed) return;
      const normalized = normalizeResources(resources);
      if (JSON.stringify(normalized) !== JSON.stringify(desired)) { desired = normalized; revision++; }
      void pump();
    },
    renew() { acknowledged = -1; void pump(); },
    dispose() { disposed = true; clearTimeout(timer); controller?.abort(); },
  };
}
